/**
 * Tools on disk: where a box's installs and package caches live (sandbox storage by lifetime §5.1,
 * box-hosts ruling R2).
 *
 * One layout under one workspace root, read by both halves. The server declares it for a Blaxel box
 * (root /app/workspace, the image's Python). A cloud machine whose runner dials out (boat) derives it
 * from its own WORKING_DIR and its own python3, so the server's /app/workspace never reaches a box
 * where that path does not exist.
 *
 * PATH is a prefix, never a whole PATH: the launcher and the login shells prepend it, so the box's own
 * PATH is never overwritten. The CPU torch index is pip's only; uv gets the torch-family selector
 * instead (Ruling R26). A cloud box has no GPU.
 */
import { VESTRA_DIR } from './lifetimes.js';

export const CPU_TORCH_INDEX = 'https://download.pytorch.org/whl/cpu';

/**
 * The one switch a cloud machine that dials out (boat) is served instead of the layout's paths: '1'
 * means "lay the tools out under your own WORKING_DIR". The server serves it
 * (runner-environment-spec.service.ts) and the runner reads it (apps/sandbox cloud-tools-env.ts), both
 * from this constant, so the two can never drift apart and silently turn a boat machine's tools off.
 */
export const TOOLS_ON_DISK_ENV = 'VESTRA_TOOLS_ON_DISK';

/**
 * Every install and cache path under `root`, for the venv tagged `pyTag` (for example 'py3.13'). The
 * order is the order Blaxel boxes have always been sent, so their env hash does not move.
 */
export function toolsLayoutEnv(root: string, pyTag: string): Array<{ name: string; value: string }> {
  const v = `${root.replace(/\/+$/, '')}/${VESTRA_DIR}`;
  const venv = `${v}/tools/${pyTag}`;
  return [
    { name: 'VESTRA_TOOLS_DIR', value: `${v}/tools` },
    { name: 'VIRTUAL_ENV', value: venv },
    { name: 'VESTRA_PATH_PREFIX', value: `${venv}/bin:${v}/tools/npm/bin:${v}/tools/bun/bin` },
    { name: 'UV_CACHE_DIR', value: `${v}/cache/uv` },
    { name: 'PIP_CACHE_DIR', value: `${v}/cache/pip` },
    { name: 'PIP_EXTRA_INDEX_URL', value: CPU_TORCH_INDEX },
    { name: 'UV_TORCH_BACKEND', value: 'cpu' },
    { name: 'npm_config_cache', value: `${v}/cache/npm` },
    { name: 'npm_config_prefix', value: `${v}/tools/npm` },
    { name: 'BUN_INSTALL', value: `${v}/tools/bun` },
    { name: 'HF_HOME', value: `${v}/cache/models` },
    { name: 'TORCH_HOME', value: `${v}/cache/models` },
  ];
}
